import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import libraryService from '../../services/libraryService';
import { Star, Heart } from '@phosphor-icons/react';

const backendBase = process.env.REACT_APP_BACKEND_URL?.replace(/\/+$/, '') || 'http://127.0.0.1:8000';

function buildUrl(path) {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${backendBase}${path.startsWith('/') ? path : `/${path}`}`;
}

function StarRatingInput({ value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          style={{ background: 'none', border: 0, cursor: 'pointer', padding: 2 }}
          aria-label={`Rate ${n} star${n > 1 ? 's' : ''}`}
        >
          <Star size={20} weight={n <= value ? 'fill' : 'regular'} color={n <= value ? '#f59e0b' : '#d1d5db'} />
        </button>
      ))}
    </div>
  );
}

function LibraryPreviewModal({ item, onClose, onChanged }) {
  const [fullscreen, setFullscreen] = useState(false);
  const [isFavorited, setIsFavorited] = useState(Boolean(item?.is_favorited));
  const [myRating, setMyRating] = useState(0);
  const [review, setReview] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);
  const viewTracked = React.useRef(null);

  useEffect(() => {
    setIsFavorited(Boolean(item?.is_favorited));
    setMyRating(0);
    setReview('');
    if (item && viewTracked.current !== item.id) {
      viewTracked.current = item.id;
      libraryService.trackView(item.id).catch(() => {});
    }
  }, [item]);

  if (!item) return null;

  const resourceUrl = buildUrl(item.file_url);
  const thumbnailUrl = buildUrl(item.thumbnail_url);

  const handleDownload = async () => {
    if (!resourceUrl) return;
    libraryService.trackDownload(item.id).catch(() => {});
    const link = document.createElement('a');
    link.href = resourceUrl;
    link.target = '_blank';
    link.rel = 'noreferrer noopener';
    link.download = item.title?.replace(/[^a-z0-9\-_.]/gi, '_') || 'resource';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    if (onChanged) onChanged();
  };

  const toggleFavorite = async () => {
    try {
      if (isFavorited) {
        await libraryService.unfavoriteItem(item.id);
        setIsFavorited(false);
        toast.success('Removed from favorites');
      } else {
        await libraryService.favoriteItem(item.id);
        setIsFavorited(true);
        toast.success('Added to favorites');
      }
      if (onChanged) onChanged();
    } catch (error) {
      toast.error(error.message || 'Unable to update favorite');
    }
  };

  const submitRating = async () => {
    if (!myRating) {
      toast.error('Select a star rating first');
      return;
    }
    setSubmittingRating(true);
    try {
      await libraryService.rateItem(item.id, { rating: myRating, review: review || null });
      toast.success('Thanks for your rating!');
      if (onChanged) onChanged();
    } catch (error) {
      toast.error(error.message || 'Unable to submit rating');
    } finally {
      setSubmittingRating(false);
    }
  };

  const renderPreview = () => {
    if (!resourceUrl) {
      if (item.external_url) {
        return <div className="empty-state">This resource is available by external link. Use Open Link to view it.</div>;
      }
      return <div className="empty-state">This resource cannot be previewed.</div>;
    }
    if (item.content_type === 'pdf' || item.file_url?.toLowerCase().endsWith('.pdf')) {
      return (
        <iframe
          src={resourceUrl}
          title={item.title}
          style={{ width: '100%', height: fullscreen ? '85vh' : '65vh', border: 0, borderRadius: 12, background: '#f3f4f6' }}
        />
      );
    }
    if (item.content_type === 'video' || item.material_type === 'educational_video') {
      return (
        <video controls preload="metadata" src={resourceUrl} style={{ width: '100%', maxHeight: fullscreen ? '85vh' : '65vh', borderRadius: 12, background: '#000' }} />
      );
    }
    if (item.content_type === 'audio') {
      return <audio controls src={resourceUrl} style={{ width: '100%' }} />;
    }
    return <div className="empty-state">This file type cannot be previewed inline. Use Open Resource to view it instead.</div>;
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 16, width: 'min(100%, 980px)', maxHeight: '92vh', overflow: 'auto', padding: 18, boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 18 }}>{item.title}</div>
            <div style={{ color: '#6b7280', fontSize: 13 }}>
              {[item.author, item.publisher, item.publication_year].filter(Boolean).join(' · ') || item.description || 'Preview resource'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-secondary" type="button" onClick={toggleFavorite} title="Toggle favorite">
              <Heart size={16} weight={isFavorited ? 'fill' : 'regular'} color={isFavorited ? '#dc2626' : undefined} />
            </button>
            <button className="btn btn-secondary" type="button" onClick={() => setFullscreen((v) => !v)}>{fullscreen ? 'Exit Fullscreen' : 'Fullscreen'}</button>
            {item.file_url && <button className="btn btn-outline" type="button" onClick={handleDownload}>Download</button>}
            <button className="btn btn-secondary" type="button" onClick={onClose}>Close</button>
          </div>
        </div>

        {thumbnailUrl && !resourceUrl && (
          <img src={thumbnailUrl} alt={item.title} style={{ width: '100%', borderRadius: 12, marginBottom: 12, maxHeight: 260, objectFit: 'cover' }} />
        )}

        {renderPreview()}

        {item.external_url && (
          <div style={{ marginTop: 10 }}>
            <a className="btn btn-primary" href={item.external_url} target="_blank" rel="noreferrer">Open Link</a>
          </div>
        )}

        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #e5e7eb', display: 'grid', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, color: '#6b7280' }}>
              {item.average_rating != null ? `${item.average_rating.toFixed(1)} / 5 (${item.ratings_count} rating${item.ratings_count === 1 ? '' : 's'})` : 'No ratings yet'}
            </span>
            <span style={{ fontSize: 13, color: '#6b7280' }}>· {item.view_count || 0} views · {item.download_count || 0} downloads</span>
          </div>
          <div style={{ display: 'grid', gap: 8 }}>
            <div style={{ fontSize: 13, color: '#374151' }}>Rate this resource</div>
            <StarRatingInput value={myRating} onChange={setMyRating} />
            <textarea className="form-control" rows="2" placeholder="Optional review" value={review} onChange={(e) => setReview(e.target.value)} />
            <button className="btn btn-primary" type="button" onClick={submitRating} disabled={submittingRating} style={{ justifySelf: 'start' }}>
              {submittingRating ? 'Submitting...' : 'Submit Rating'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LibraryPreviewModal;
